import React, { useState, useEffect, useRef } from "react";
import { findInitialSchoolYear } from "../../../util/schoolYear";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../../context/AuthContext";
import { useUIContext } from "../../../context/UIContext";
import useSurveys from "../../../hooks/useSurveys";
import useLessonStudents from "../../../hooks/useLessonStudentData";
import useTermPeriod from "../../../hooks/useTermPeriod";
import useOrganizationSetting from "../../../hooks/useOrganizationSetting";
import useSurveyResponses from "../../../hooks/useSurveyResponses";
import useSurveyQuestions from "../../../hooks/useSurveyQuestions";
import { setTermIdByToday } from "../../../util/dateRelated";
import DropdownSchoolYear from "../../../unicomponents/Dropdowns/DropdownSchoolYear";
import DropdownTermDate from "../../../unicomponents/Dropdowns/DropdownTermDate";
import SurveyForm from "./components/SurveyForm";

export default function SubjectSurvey() {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { handleToastCenterTop } = useUIContext();

  // 기본 조건 설정
  const conditions = { schoolid: user?.schoolid };

  // 상태 관리
  const [filters, setFilters] = useState({
    schoolYear: null,
    schoolYearId: "",
    selectedSurveyId: "",
    selectedSubjectId: "",
  });

  const [termId, setTermId] = useState("");
  const [currentPeriod, setCurrentPeriod] = useState({});
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  // Hook 사용
  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting(conditions);
  const {
    termPeriodQuery: { data: termPeriodData },
  } = useTermPeriod(conditions);
  const { getAllLessonStudentsByStudentId } = useLessonStudents();

  // useRef로 함수 참조 고정
  const getAllLessonStudentsByStudentIdRef = useRef(
    getAllLessonStudentsByStudentId
  );

  // 설문 조회 조건 - bySubject이고 student 대상인 설문만
  const surveyConditions = {
    schoolid: user?.schoolid,
    survey_type: "bySubject",
    response_target: "student",
  };
  const {
    surveysQuery: { data: surveysData },
  } = useSurveys(surveyConditions);

  // 응답 조회 조건 - 조건이 모두 유효할 때만 활성화
  const responseConditions = {
    schoolid: user?.schoolid,
    username: user?.username, // 백엔드에서 username으로 통일됨
    // survey_id: filters.selectedSurveyId,
  };
  const {
    surveyResponsesQuery: { data: responsesData },
  } = useSurveyResponses(
    user?.username && filters.schoolYear
      ? // && filters.selectedSurveyId
        responseConditions
      : {}
  );

  // 설문 질문 조회 조건 - 설문과 과목이 모두 선택된 경우
  const questionsConditions = {
    schoolid: user?.schoolid,
    survey_id: filters.selectedSurveyId,
    subject_id: filters.selectedSubjectId,
  };

  const {
    surveyQuestionsBySubjectQuery: { data: questionsData },
  } = useSurveyQuestions(
    filters.selectedSurveyId && filters.selectedSubjectId
      ? questionsConditions
      : {}
  );

  // 초기 학년도/학기 설정 - 설문이 선택된 경우에만
  // useEffect(() => {
  //   if (
  //     filters.selectedSurveyId &&
  //     filters.schoolYear === null &&
  //     settingData?.length > 0
  //   ) {
  //     const initialData = settingData.sort(
  //       (a, b) => compareSchoolYearDesc(a.school_year, b.school_year)
  //     )[0];
  //     const initialSchoolYear = initialData.school_year;
  //     const initialSchoolYearId = initialData.id;

  //     setFilters((prev) => ({
  //       ...prev,
  //       schoolYear: initialSchoolYear,
  //       schoolYearId: initialSchoolYearId,
  //     }));

  //     if (termPeriodData) {
  //       setTermIdByToday(termPeriodData, setTermId, initialSchoolYearId);
  //     }
  //   }
  // }, [
  //   settingData,
  //   termPeriodData,
  //   filters.schoolYear,
  //   filters.selectedSurveyId,
  // ]);

  // 변경 후
  useEffect(() => {
    if (settingData?.length > 0 && filters.schoolYear === null) {
      const initialData = findInitialSchoolYear(settingData)?.row;
      if (!initialData) return;
      const initialSchoolYear = initialData.school_year;
      const initialSchoolYearId = initialData.id;

      setFilters((prev) => ({
        ...prev,
        schoolYear: initialSchoolYear,
        schoolYearId: initialSchoolYearId,
      }));

      if (termPeriodData) {
        setTermIdByToday(termPeriodData, setTermId, initialSchoolYearId);
      }
    }
  }, [settingData, termPeriodData, filters.schoolYear]);

  // 현재 학기 정보 설정
  useEffect(() => {
    if (termPeriodData && termId) {
      const period = termPeriodData.find(
        (p) => p.id.toString() === termId.toString()
      );
      setCurrentPeriod(period || {});
    }
  }, [termPeriodData, termId]);

  // 학생 수강 과목 조회
  useEffect(() => {
    if (user?.username && filters.schoolYearId && termId) {
      const forWhere = {
        schoolid: user.schoolid,
        username: user.username,
      };

      getAllLessonStudentsByStudentIdRef.current.mutate(forWhere, {
        onSuccess: (data) => {
          const filteredData = data.filter(
            (item) =>
              item.school_year_id.toString() ===
                filters.schoolYearId.toString() &&
              item.lesson?.term_id.toString() === termId.toString()
          );
          setAvailableSubjects(filteredData);
        },
        onError: (error) => {
          console.error("과목 조회 오류:", error);
          handleToastCenterTop(
            t("common.messages.errorAlert") + error.message,
            3000,
            "#dc3545"
          );
        },
      });
    }
  }, [user, filters.schoolYearId, termId, handleToastCenterTop, t]);

  // 드롭다운 변경 핸들러
  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0]?.text || "";

    if (name === "0") {
      // 학년도 변경 - 하위 선택들 초기화
      setFilters((prev) => ({
        ...prev,
        schoolYear: selectedOptionText,
        schoolYearId: value,
        selectedSubjectId: "",
        selectedSurveyId: "",
      }));
      setTermId("");
      setTermIdByToday(termPeriodData, setTermId, value);
      setSelectedSurvey(null);
    }

    if (name === "termDate") {
      // 학기 변경 - 과목 선택 초기화
      setTermId(value);
      setFilters((prev) => ({
        ...prev,
        selectedSubjectId: "",
        selectedSurveyId: "",
      }));
      setSelectedSurvey(null);
    }

    if (name === "subject") {
      // 과목 변경 - 선택한 과목에 따라 설문 질문들을 로드할 예정
      setFilters((prev) => ({
        ...prev,
        selectedSubjectId: value,
        selectedSurveyId: "",
      }));
      setSelectedSurvey(null);
    }

    if (name === "survey") {
      // 설문 변경 - 다른 모든 선택 초기화
      setFilters((prev) => ({
        ...prev,
        selectedSurveyId: value,
      }));

      const selectedSurvey = surveysData?.find(
        (s) => s.id.toString() === value
      );
      setSelectedSurvey(selectedSurvey);
    }
  };

  // 과목별 완료 상태 계산
  const getSubjectsWithStatus = () => {
    if (!availableSubjects.length) return [];

    return availableSubjects
      .filter((subject) => subject?.lesson_id)
      .map((subject) => {
        // 1. 해당 과목에 연결된 공개 설문들 찾기
        const publishedSurveys =
          surveysData?.filter((survey) => {
            const subjectLink = survey.subjectLinks?.find(
              (link) =>
                String(link.subject_id) === String(subject.lesson_id) &&
                link.in_use === true
            );
            return (
              subjectLink &&
              (survey.status === "respondable" || survey.status === "readable")
            );
          }) || [];

        // 2. 해당 과목의 공개 설문들에 대한 응답 상태 확인
        const completedSurveys = publishedSurveys.filter((survey) => {
          return responsesData?.some(
            (response) =>
              String(response.survey_id) === String(survey.id) &&
              String(response.subject_id) === String(subject.lesson_id) &&
              response.is_submitted === true
          );
        });

        // 3. 모든 공개 설문이 완료되었는지 확인
        const isCompleted =
          publishedSurveys.length > 0 &&
          completedSurveys.length === publishedSurveys.length;

        return {
          ...subject,
          publishedSurveysCount: publishedSurveys.length,
          completedSurveysCount: completedSurveys.length,
          isCompleted,
        };
      })
      .sort((a, b) => {
        if (a.isCompleted && !b.isCompleted) return 1;
        if (!a.isCompleted && b.isCompleted) return -1;
        return (
          a.lesson?.subject_name?.localeCompare(b.lesson?.subject_name) || 0
        );
      });
  };

  const subjectsWithStatus = getSubjectsWithStatus();

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          {/* 설문 선택 카드 */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row">
                {/* 학년도 선택 */}
                <div className="col-md-2 mb-2">
                  <label className="form-label">
                    {t("common.survey.schoolYear", "학년도")}
                  </label>
                  <DropdownSchoolYear
                    settingData={settingData}
                    handleDropdownChange={handleDropdownChange}
                    schoolYearId={filters.schoolYearId}
                    allYears={false}
                    disabled={false}
                    title={t("common.survey.selectOption", "선택하세요")}
                  />
                </div>

                {/* 학기 선택 */}
                <div className="col-md-2 mb-2">
                  <label className="form-label">
                    {t("common.survey.term", "학기")}
                  </label>
                  <DropdownTermDate
                    termPeriodData={termPeriodData}
                    handleDropdownChange={handleDropdownChange}
                    termId={termId}
                    schoolYearId={filters.schoolYearId}
                    disabled={!filters.schoolYearId}
                    isTitle={true}
                  />
                </div>

                {/* 과목 선택 */}
                <div className="col-md-4 mb-4">
                  <label className="form-label">
                    {t("common.survey.subject", "과목")}
                  </label>
                  <select
                    name="subject"
                    className="form-control"
                    value={filters.selectedSubjectId}
                    onChange={handleDropdownChange}
                    disabled={!termId}
                  >
                    <option value="">
                      {subjectsWithStatus.length > 0
                        ? t("common.survey.selectOption", "선택하세요")
                        : t(
                            "common.survey.noSubjectAvailable",
                            "수강 과목이 없습니다"
                          )}
                    </option>
                    {subjectsWithStatus.map((subject) => (
                      <option
                        key={subject.id}
                        value={subject?.lesson_id || ""}
                        style={{
                          backgroundColor: subject.isCompleted
                            ? "#d4edda"
                            : "transparent",
                          color: subject.isCompleted ? "#155724" : "inherit",
                        }}
                      >
                        {subject?.lesson?.subject_name || "알 수 없는 과목"}
                        {subject.isCompleted
                          ? ` (${t("common.survey.completed", "완료")})`
                          : ` (${t("common.survey.incomplete", "미완료")})`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 설문 선택  */}
                <div className="col-md-4 mb-4">
                  <label className="form-label">
                    {t("common.survey.selectSurvey", "설문지 선택")}
                  </label>
                  <select
                    name="survey"
                    className="form-control"
                    value={filters.selectedSurveyId}
                    onChange={handleDropdownChange}
                    disabled={!filters.selectedSubjectId}
                  >
                    <option value="">
                      {surveysData?.filter((survey) => {
                        const isPublished = survey.subjectLinks?.find(
                          (link) => {
                            return (
                              String(link.subject_id) ===
                                String(filters.selectedSubjectId) && link.in_use
                            );
                          }
                        );
                        return (
                          (survey.status === "respondable" ||
                            survey.status === "readable") &&
                          isPublished
                        );
                      }).length > 0
                        ? t("common.survey.selectOption", "선택하세요")
                        : t(
                            "common.survey.noSurveyAvailable",
                            "수업 설문이 없습니다"
                          )}
                    </option>
                    {surveysData
                      ?.filter((survey) => {
                        const isPublished = survey.subjectLinks?.find(
                          (link) => {
                            return (
                              String(link.subject_id) ===
                                String(filters.selectedSubjectId) && link.in_use
                            );
                          }
                        );
                        return (
                          (survey.status === "respondable" ||
                            survey.status === "readable") &&
                          isPublished
                        );
                      })
                      .map((survey) => (
                        <option key={survey.id} value={survey.id}>
                          {survey.title}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* 설문 응답 폼 */}
          {selectedSurvey &&
          filters.selectedSubjectId &&
          questionsData &&
          questionsData.length > 0 ? (
            <SurveyForm
              survey={selectedSurvey}
              questions={questionsData}
              subjectId={filters.selectedSubjectId}
              subjectName={
                subjectsWithStatus.find(
                  (s) =>
                    s?.lesson_id?.toString() ===
                    filters.selectedSubjectId?.toString()
                )?.lesson?.subject_name || "알 수 없는 과목"
              }
              subjectInfo={subjectsWithStatus.find(
                (s) =>
                  s?.lesson_id?.toString() ===
                  filters.selectedSubjectId?.toString()
              )}
              schoolYear={filters.schoolYear}
              schoolTerm={currentPeriod?.term_name}
              currentPeriod={currentPeriod}
              respondentUsername={user?.username}
            />
          ) : selectedSurvey && filters.selectedSubjectId ? (
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">{selectedSurvey.title}</h5>
                <p className="card-text">{selectedSurvey.description}</p>
                <div className="alert alert-info">
                  <strong>
                    {t("common.survey.selectedSubject", "선택된 과목")}:
                  </strong>{" "}
                  {subjectsWithStatus.find(
                    (s) =>
                      s?.lesson_id?.toString() ===
                      filters.selectedSubjectId?.toString()
                  )?.lesson?.subject_name || "알 수 없는 과목"}
                </div>

                {/* 설문 질문 로딩 상태 */}
                {filters.selectedSubjectId ? (
                  <div className="alert alert-warning">
                    <strong>
                      {t(
                        "common.survey.noQuestionsFound",
                        "이 과목에 대한 설문 질문이 없습니다."
                      )}
                    </strong>
                  </div>
                ) : (
                  <div className="alert alert-warning">
                    <strong>
                      {t(
                        "common.survey.selectSubjectFirst",
                        "과목을 먼저 선택해주세요."
                      )}
                    </strong>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* 설문이 선택되지 않은 경우 안내 메시지 */}
          {!selectedSurvey && (
            <div className="card">
              <div className="card-body text-center">
                <h5 className="card-title text-muted">
                  {t(
                    "common.survey.pleaseSelectSurvey",
                    "과목을 선택하고 설문을 선택해주세요"
                  )}
                </h5>
                <p className="card-text text-muted">
                  {t(
                    "common.survey.selectSurveyInstruction",
                    "학년도 → 학기 → 과목 → 설문 순서로 선택하시면 설문 응답을 시작할 수 있습니다."
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
